import type { AppState, CapacityUnit, Order } from '../types'

export const money = (value: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(value || 0))

export const pct = (value: number): string => `${Math.round(Number(value || 0) * 100)}%`

export const num = (value: unknown): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

export function unitLabor(unit: CapacityUnit): number {
  return ['art', 'setup', 'front', 'flip', 'back', 'finish', 'package'].reduce((total, key) => total + num(unit[key as keyof CapacityUnit]), 0)
}

export function unitCash(unit: CapacityUnit, state: AppState): number {
  const settings = state.settings
  return (num(unit.material) + settings.packaging + settings.electricSession / Math.max(1, settings.unitsSession)) * (1 + settings.failureReserve)
}

export function capacityStats(state: AppState) {
  const capacity = state.capacity
  const attempted = capacity.filter((unit) => unit.date || unit.design || unit.material || unit.pass).length
  const sellable = capacity.filter((unit) => unit.pass === 'Pass').length
  const totalCash = capacity.reduce((total, unit) => total + unitCash(unit, state), 0)
  const totalLabor = capacity.reduce((total, unit) => total + unitLabor(unit), 0)
  const totalMachine = capacity.reduce((total, unit) => total + num(unit.machine), 0)
  return {
    attempted,
    sellable,
    defect: attempted ? 1 - sellable / attempted : 0,
    avgCash: attempted ? totalCash / attempted : 0,
    avgEconomic: attempted ? (totalCash + totalLabor / 60 * state.settings.laborHour) / attempted : 0,
    avgLabor: attempted ? totalLabor / attempted : 0,
    avgMachine: attempted ? totalMachine / attempted : 0
  }
}

export function orderCalc(order: Order, state: AppState) {
  const revenue = num(order.qty) * num(order.unitPrice)
  const proc = revenue * state.settings.processingRate + state.settings.processingFixed
  const cash = num(order.material) + num(order.packaging) + num(order.other) + num(order.remake) + proc
  const before = revenue - cash
  const tax = Math.max(0, before) * state.settings.taxReserve
  const pocket = before - tax
  return { revenue, proc, cash, before, tax, pocket }
}

export function gateEvidence(state: AppState) {
  const stats = capacityStats(state)
  const advanced = state.leads.filter((lead) => lead.status !== 'Not Contacted').length
  const authority = state.leads.filter((lead) => ['Connected', 'Demo Scheduled', 'Quoted', 'Negotiating', 'Won'].includes(lead.status)).length
  const paid = state.orders.some((order) => ['Deposit Paid', 'In Production', 'Proof Approval', 'Ready', 'Delivered', 'Complete'].includes(order.status))
  const profitable = state.orders.some((order) => order.status === 'Complete' && orderCalc(order, state).pocket > 0 && order.repeat === 'Yes')
  return [
    { label: 'Ten representative units attempted', done: stats.attempted >= 10, view: 'capacity' },
    { label: 'Measured unit economics calculated', done: stats.attempted >= 10, view: 'capacity' },
    { label: 'Three sales-ready samples selected', done: Boolean(state.manualChecks.samples), manual: 'samples' },
    { label: 'Three offers approved for testing', done: Boolean(state.manualChecks.offers), manual: 'offers' },
    { label: 'Ten prospects contacted', done: advanced >= 10, view: 'leads' },
    { label: 'Three purchasing authorities reached', done: authority >= 3, view: 'leads' },
    { label: 'Paid B2B pilot deposit received', done: paid, view: 'orders' },
    { label: 'Profitable delivery + reorder opportunity', done: profitable, view: 'orders' }
  ] as const
}

export function nextAction(state: AppState) {
  const first = gateEvidence(state).find((item) => !item.done)
  if (!first) return { type: 'Scale deliberately', text: 'The launch gate is complete. Standardize the winning offer and secure the next repeat order.', view: 'command' }
  const map: Record<string, [string, string]> = {
    'Ten representative units attempted': ['Production', 'Make and log the next representative double-sided unit.'],
    'Measured unit economics calculated': ['System', 'Review measured cash cost, economic cost, defect rate, and safe capacity.'],
    'Three sales-ready samples selected': ['Founder taste', 'Choose the three finished samples Jay is willing to put his name on.'],
    'Three offers approved for testing': ['Offer', 'Approve the single, personalized, and paid-pilot offers.'],
    'Ten prospects contacted': ['Revenue', 'Contact the next ranked purchasing authority and record the result.'],
    'Three purchasing authorities reached': ['Revenue', 'Follow up until three real buyers, not general inboxes, respond.'],
    'Paid B2B pilot deposit received': ['Close', 'Present the three-card paid pilot and ask directly for the deposit.'],
    'Profitable delivery + reorder opportunity': ['Delivery', 'Complete the pilot, record pocket cash, then ask for the next order.']
  }
  const [type, text] = map[first.label] ?? ['System', first.label]
  return { type, text, view: 'view' in first ? first.view : 'command', manual: 'manual' in first ? first.manual : undefined }
}
